import Vue from 'vue';
import firebase from 'firebase/app';
import 'firebase/firestore';

import { getLocalTime, isValidDate } from '@qualle-admin/qutil/dist/date';
import { progress } from '@/service/progress';
import { hasTerminalAddress } from '@/validators/terminalAddress';

import { delay, values, uniqBy } from 'lodash';

import { getCenter, getDistance } from 'geolib/es';

import { getNearestLocation } from '@qualle-admin/qutil/dist/region';

import { v4 as uuid } from 'uuid';

import selectedItemsFixture from '@/fixtures/selected-items.json';
import { processRequests } from '@/service/protocol';
import { filterDocuments } from '@/service/documentFilters';

const state = {
  turns: [],
  tour_turns: [],
  snapshots: [],
  locations: {
    turns: [],
    containers: [],
    snapshots: [],
  },
  error: null,
  progress: 0,
  is_scac_in_org: false,
  secondary_carrier_turns: [],
  secondary_carrier_snapshots: [],
};

const mutations = {
  set_turns_upload_progress(state, payload) {
    state.progress = payload;
  },
  set_turns(state, payload) {
    state.turns = payload;
  },
  set_tour_turns(state, payload) {
    state.tour_turns = payload;
  },
  set_turns_snapshots(state, payload) {
    state.snapshots = payload;
  },
  set_secondary_carrier_snapshots(state, payload) {
    state.secondary_carrier_snapshots = payload;
  },
  set_locations_data(state, payload) {
    state.locations = payload;
  },
  update_filtered_turns(state, payload) {
    state.turns = payload;
  },
  update_error(state, payload) {
    state.error = payload;
  },
  update_scac(state, payload) {
    state.is_scac_in_org = payload;
  },
  set_secondary_carrier_turns(state, payload) {
    state.secondary_carrier_turns = payload;
  },
};

const actions = {
  get_all_turns: async ({ commit, rootGetters }, payload) => {
    let orgId;

    // const debouncedSetTurns = debounce(({ turns }) => {
    //   commit('set_turns', turns);
    // }, 500);

    if (payload) {
      orgId = payload;
    } else if (
      rootGetters['auth/get_user'] &&
      typeof rootGetters['auth/get_user'].orgId !== 'undefined'
    ) {
      orgId = rootGetters['auth/get_user'].orgId;
    }

    let turns = [];
    if (typeof orgId !== 'undefined') {
      const { docs: organizationDocs } = await firebase
        .firestore()
        .collection('organizations')
        .where('agency_orgid', '==', orgId)
        .get();
      if (organizationDocs.length) {
        const orgData = organizationDocs.map((doc) => doc.data());

        orgData.forEach((org) => {
          if (typeof org !== 'undefined') {
            Vue.prototype.$snapshots.$turns_snapshot = firebase
              .firestore()
              .collection('turns')
              .where('orgId', '==', org.orgId)
              .onSnapshot(async ({ docs: docsData = [] }) => {
                const allTurns = rootGetters['turns/get_turns'] || [];
                const docs = filterDocuments(docsData);
                const agentTurns = docs.map((doc) => ({
                  ...doc.data(),
                  id: doc.id,
                }));
                turns = uniqBy(
                  [...agentTurns, ...allTurns],
                  ({ turn_id: turnId }) => turnId
                );
                await commit('set_turns', turns);
                turns = [];
              });
          }
        });
      }

      Vue.prototype.$snapshots.$turns_snapshot = firebase
        .firestore()
        .collection('turns')
        .where('orgId', '==', orgId)
        .onSnapshot(
          async ({ docs: docsData = [] }) => {
            const docs = filterDocuments(docsData);
            const orgTurns = docs.map((doc) => ({ ...doc.data(), id: doc.id }));
            const allTurns = rootGetters['turns/get_turns'] || [];
            turns = uniqBy(
              [...orgTurns, ...allTurns],
              ({ turn_id: turnId }) => turnId
            );
            await commit('set_turns', turns);
            turns = [];
          },
          (error) => {
            commit('update_error', error);
          }
        );
    }
  },
  get_turns_snapshots: async ({ commit, rootGetters }, payload) => {
    let orgId;

    if (payload) {
      orgId = payload;
    } else if (
      rootGetters['auth/get_user'] &&
      typeof rootGetters['auth/get_user'].orgId !== 'undefined'
    ) {
      orgId = rootGetters['auth/get_user'].orgId;
    }

    let allSnapshots = [];
    if (typeof orgId !== 'undefined') {
      const { docs: organizationDocs } = await firebase
        .firestore()
        .collection('organizations')
        .where('agency_orgid', '==', orgId)
        .get();
      if (organizationDocs.length) {
        const filterDocs = filterDocuments(organizationDocs);
        const orgData = filterDocs.map((doc) => doc.data());

        orgData.forEach((org) => {
          if (typeof org !== 'undefined') {
            // Getting agent turn snapshots
            Vue.prototype.$snapshots.$turns_snapshot = firebase
              .firestore()
              .collection('snapshots')
              .where('orgId', '==', org.orgId)
              .onSnapshot(
                ({ docs = [] }) => {
                  const allTurnsSnapshots =
                    rootGetters['turns/get_turns_snapshots'] || [];
                  const snapshots = docs.map((doc) => ({
                    id: doc.data().turn_id,
                    ...doc.data(),
                  }));
                  allSnapshots = uniqBy(
                    [...snapshots, ...allTurnsSnapshots],
                    ({ turn_id: turnId }) => turnId
                  );
                  commit('set_turns_snapshots', allSnapshots);
                  allSnapshots = [];
                },
                (error) => {
                  commit('update_error', error);
                }
              );
          }
        });
      }

      if (typeof orgId !== 'undefined') {
        Vue.prototype.$snapshots.$snapshots_snapshot = firebase
          .firestore()
          .collection('snapshots')
          .where('orgId', '==', orgId)
          .onSnapshot(
            ({ docs = [] }) => {
              const allTurnsSnapshots =
                rootGetters['turns/get_turns_snapshots'] || [];
              const filterDocs = filterDocuments(docs);
              const snapshots = filterDocs.map((doc) => ({
                id: doc.data().turn_id,
                ...doc.data(),
              }));
              allSnapshots = uniqBy(
                [...snapshots, ...allTurnsSnapshots],
                ({ turn_id: turnId }) => turnId
              );
              commit('set_turns_snapshots', allSnapshots);
              allSnapshots = [];
            },
            (error) => {
              commit('update_error', error);
            }
          );
      }
    }
  },
  get_all_locations_data: async ({ commit, rootGetters }, payload) => {
    let orgId;

    if (payload) {
      orgId = payload;
    } else if (
      rootGetters['auth/get_user'] &&
      typeof rootGetters['auth/get_user'].orgId !== 'undefined'
    ) {
      orgId = rootGetters['auth/get_user'].orgId;
    }

    if (typeof orgId !== 'undefined') {
      const [containers, turns, snapshots] = [
        await firebase
          .firestore()
          .collection('containers')
          .where('orgId', '==', orgId)
          .get(),
        await firebase
          .firestore()
          .collection('turns')
          .where('orgId', '==', orgId)
          .get(),
        await firebase
          .firestore()
          .collection('snapshots')
          .where('orgId', '==', orgId)
          .get(),
      ];

      const fContainers = filterDocuments(containers.docs);
      const fTurns = filterDocuments(turns.docs);
      const fSnapshots = filterDocuments(snapshots.docs);

      const locationsData = {
        containers: fContainers.map((doc) => ({ id: doc.id, ...doc.data() })),
        turns: fTurns.map((doc) => ({ id: doc.id, ...doc.data() })),
        snapshots: fSnapshots.map((doc) => ({ id: doc.id, ...doc.data() })),
      };

      commit('set_locations_data', locationsData);
    }
  },
  create_turn: async ({ commit, dispatch, rootGetters }, payload) => {
    const batch = firebase.firestore().batch();
    const { mc, orgId } = rootGetters['auth/get_user'];
    const orgDoc = await firebase
      .firestore()
      .collection('organizations')
      .doc(`${rootGetters['auth/get_user'].orgId}`)
      .get();
    const { company } = orgDoc.data();

    if (hasTerminalAddress(payload)) {
      const message = 'We do not allow terminal addresses';

      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          message,
          error: true,
        },
        { root: true }
      );

      throw new Error(message);
    } else {
      try {
        const turnsToCreate = payload.map(
          async ({
            ref,
            container,
            amount,
            amountBreakup = null,
            lfd = getLocalTime().format(),
            chassis,
            chassisType = 'private',
            line,
            scac = rootGetters['auth/get_org'].scac,
            size,
            location,
            companyLocation,
            exportLocation,
            requestDetails = {
              type: 'same_carrier_request',
              secondaryCarrierScac: null,
              secondaryCarrierEmail: null,
            },
            exportPool = null,
            scheduleTurn = null,
            scheduleDate = null,
          }) => {
            const turnRef = await firebase
              .firestore()
              .collection('turns')
              .doc(`${container}_${rootGetters['auth/get_user'].orgId}`);
            const turnData = (await turnRef.get()).data();
            const turnId = turnData?.turnId || uuid();
            const created = getLocalTime().format();
            const portLocation =
              companyLocation && companyLocation.name
                ? companyLocation
                : getNearestLocation(exportLocation)[0].terminals[0];

            const formattedPortLocation = {
              ...portLocation,
              market: getNearestLocation(portLocation)[0].market,
            };

            // set snapshot
            const snapshotRef = await firebase
              .firestore()
              .collection('snapshots')
              .doc(turnId);

            const snapshotDoc = await snapshotRef.get();
            const newTurnData = {
              turn_id: turnId,
              ref,
              container,
              company,
              amount: amount || 0,
              amountBreakup: amountBreakup || null,
              status: scheduleTurn ? 'requestScheduled' : 'pending',
              created,
              lfd: getLocalTime(lfd).endOf('day').format(),
              description: null,
              mc,
              orgId,
              scac: scac || rootGetters['auth/get_org'].scac,
              size,
              owner: rootGetters['auth/get_user'].user_id,
              chassis,
              chassisType,
              line,
              location,
              exportLocation,
              companyLocation: formattedPortLocation,
              name: rootGetters['auth/get_user'].name,
              requestDetails,
              exportPool,
              scheduleTurn,
              scheduleDate,
            };

            const {
              location: { lat: lLat, lng: lLng },
              exportLocation: { lat: eLat, lng: eLng },
              companyLocation: { lat: cLat, lng: cLng },
            } = newTurnData;

            const distance1 = getDistance(
              { latitude: lLat, longitude: lLng },
              { latitude: cLat, longitude: cLng }
            );

            const distance2 = getDistance(
              { latitude: cLat, longitude: cLng },
              { latitude: eLat, longitude: eLng }
            );

            const center = getCenter([
              { latitude: lLat, longitude: lLng },
              { latitude: eLat, longitude: eLng },
              { latitude: cLat, longitude: cLng },
            ]);

            const totalDistance = distance1 + distance2;

            const preparedTurnData = {
              ...newTurnData,
              location: {
                ...location,
                distance: totalDistance,
                center,
              },
              status: scheduleTurn ? 'requestScheduled' : 'pending',
            };

            preparedTurnData.companyLocation.market = preparedTurnData
              .companyLocation.market
              ? preparedTurnData.companyLocation.market
              : null;

            if (!snapshotDoc.exists) {
              batch.set(snapshotRef, preparedTurnData);
            } else {
              batch.update(snapshotRef, preparedTurnData);
            }

            batch.set(turnRef, preparedTurnData);

            return preparedTurnData;
          }
        );

        const update = (progress, total) => {
          const completed = Math.round((progress / total) * 100);

          commit('set_turns_upload_progress', completed);
        };

        progress(turnsToCreate, update);

        const turnRequests = await Promise.all(turnsToCreate);

        await batch.commit();

        processRequests(turnRequests, dispatch);

        dispatch('containers/get_organization_containers', null, {
          root: true,
        });

        delay(() => commit('set_turns_upload_progress', 0), 1500);

        dispatch(
          'auth/new_notification',
          {
            type: 'notification',
            message: `${turnRequests.length} Turn${
              turnRequests.length > 1 ? 's' : ''
            } Created!`,
          },
          { root: true }
        );
      } catch (error) {
        commit('update_error', error);
      }
    }
  },
  cancel_turn: async ({ commit, dispatch, rootGetters }, payload) => {
    const deleteRequests = payload.map(async ({ container, orgId }) => {
      await firebase
        .firestore()
        .collection('turns')
        .doc(`${container}_${orgId}`)
        .update({ status: 'canceled' });

      dispatch('containers/get_organization_containers', null, {
        root: true,
      });
    });

    return Promise.all(deleteRequests)
      .then(() => {
        dispatch(
          'auth/new_notification',
          {
            type: 'notification',
            message: `Canceled ${payload.length} turn(s)`,
          },
          { root: true }
        );
      })
      .catch((error) => {
        commit('update_error', error);
      });
  },
  new_comment: async ({ commit, dispatch, rootGetters }, payload) => {
    const users = rootGetters['auth/get_org'].users;
    const mentions = [];
    // Getting mentioned user names from the comment
    values(users).map((val) => {
      const mentionName = '@' + val.name;
      if (payload.comment.includes(mentionName)) {
        mentions.push(mentionName);
      }
    });
    const { comment, status, container } = payload;

    let orgId;
    if (
      rootGetters['auth/get_user'] &&
      typeof rootGetters['auth/get_user'].orgId !== 'undefined'
    ) {
      orgId = rootGetters['auth/get_user'].orgId;
    } else {
      orgId = payload;
    }

    try {
      await firebase
        .firestore()
        .collection('comments')
        .doc()
        .set({
          orgId: rootGetters['auth/get_user'].orgId,
          owner: rootGetters['auth/get_user'].user_id,
          comment,
          status,
          mentions: [...mentions],
          date: getLocalTime().format(),
          container,
          turn_id: `${container}_${orgId}`,
          name: rootGetters['auth/get_user'].name,
        });

      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          message: 'New comment posted',
        },
        { root: true }
      );
    } catch (error) {
      commit('update_error', error);
    }
  },
  set_filtered_turns: async (
    { commit, rootGetters },
    { value, orgId: tourMc }
  ) => {
    let orgId;

    if (tourMc) {
      orgId = tourMc;
    } else if (
      rootGetters['auth/get_user'] &&
      typeof rootGetters['auth/get_user'].orgId !== 'undefined'
    ) {
      orgId = rootGetters['auth/get_user'].orgId;
    }
    let preparedTurns;

    let turns = [];
    if (typeof orgId !== 'undefined') {
      const { docs: organizationDocs } = await firebase
        .firestore()
        .collection('organizations')
        .where('agency_orgid', '==', orgId)
        .get();
      // Getting agent turns
      if (organizationDocs.length) {
        const orgData = organizationDocs.map((doc) => doc.data());

        orgData.forEach((org) => {
          if (typeof org !== 'undefined') {
            firebase
              .firestore()
              .collection('turns')
              .where('orgId', '==', org.orgId)
              .get()
              .then(({ docs }) => {
                const fDocs = filterDocuments(docs);

                const agentTurns = fDocs.map((doc) => ({
                  ...doc.data(),
                  id: doc.id,
                }));
                turns = [...agentTurns, ...turns];
              });
          }
        });
      }

      await firebase
        .firestore()
        .collection('turns')
        .where('orgId', '==', orgId)
        .get()
        .then(({ docs }) => {
          const fDocs = filterDocuments(docs);
          preparedTurns = fDocs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          preparedTurns = [...preparedTurns, ...turns];
          const filteredTurns = preparedTurns.filter(
            ({ container, ref, lfd }) => {
              if (isValidDate(value)) {
                return getLocalTime(lfd).isSame(getLocalTime(value), 'day');
              } else {
                return (
                  new RegExp(value, 'gi').test(container) ||
                  new RegExp(value, 'gi').test(ref)
                );
              }
            }
          );

          commit('update_filtered_turns', filteredTurns);
        })
        .catch((error) => {
          commit('update_error', error);
        });
    }
  },
  set_tour_turns: async ({ commit }, value) => {
    commit('set_tour_turns', value || selectedItemsFixture);
  },
  check_scac_in_org: ({ commit }, payload) => {
    const docRef = firebase
      .firestore()
      .collection('organizations')
      .where('scac', '==', payload);

    return docRef.get().then((doc) => {
      commit('update_scac', !doc.empty);
      return !doc.empty;
    });
  },
  set_secondary_carrier_turns: async ({ commit, rootGetters }) => {
    const scac = rootGetters['auth/get_org']?.scac;
    if (scac) {
      firebase
        .firestore()
        .collection('turns')
        .where('requestDetails.secondaryCarrierScac', '==', scac)
        .onSnapshot(({ docs }) => {
          if (docs.length) {
            const fDocs = filterDocuments(docs);
            const data = fDocs.map((val) => ({
              id: val.id,
              ...val.data(),
            }));
            commit('set_secondary_carrier_turns', data);
            return data;
          } else {
            return [];
          }
        });
    }
  },
  set_secondary_carrier_snapshots: async ({ commit, rootGetters }) => {
    const scac = rootGetters['auth/get_org']?.scac;
    if (scac) {
      firebase
        .firestore()
        .collection('snapshots')
        .where('requestDetails.secondaryCarrierScac', '==', scac)
        .onSnapshot(({ docs }) => {
          if (docs.length) {
            const fDocs = filterDocuments(docs);
            const data = fDocs.map((val) => ({ id: val.id, ...val.data() }));
            commit('set_secondary_carrier_snapshots', data);
            return data;
          } else {
            return [];
          }
        });
    }
  },
  set_filtered_secondary_carrier_turns: async (
    { commit, rootGetters },
    { value }
  ) => {
    const scac = rootGetters['auth/get_org']?.scac;
    if (scac) {
      firebase
        .firestore()
        .collection('turns')
        .where('requestDetails.secondaryCarrierScac', '==', scac)
        .onSnapshot(({ docs }) => {
          if (docs.length) {
            const fDocs = filterDocuments(docs);
            const preparedTurns = fDocs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            const filteredTurns = preparedTurns.filter(
              ({ container, ref, lfd }) => {
                if (isValidDate(value)) {
                  return getLocalTime(lfd).isSame(getLocalTime(value), 'day');
                } else {
                  return (
                    new RegExp(value, 'gi').test(container) ||
                    new RegExp(value, 'gi').test(ref)
                  );
                }
              }
            );
            commit('set_secondary_carrier_turns', filteredTurns);
          } else {
            return [];
          }
        });
    }
  },

  // uploading pod document in documents collection
  upload_pod: async ({ commit, dispatch, rootGetters }, payload) => {
    const { turnDocId, name, size, src, type, entity } = payload;
    const turnRef = firebase.firestore().collection('turns').doc(turnDocId);
    const turnDoc = await (await turnRef.get()).data();
    let podId = null;
    const data = {
      src,
      name,
      size,
      type,
    };
    const currentDate = getLocalTime().format();

    if (turnDoc.podDocId) {
      podId = turnDoc.podDocId;
      data.updated = currentDate;
    } else {
      podId = uuid();
      data.created = currentDate;
      data.updated = currentDate;
      data.turn_id = turnDoc.turn_id;
      turnRef.update({
        podDocId: podId,
      });
    }
    // sending notification
    await firebase
      .firestore()
      .collection('documents')
      .doc(podId)
      .set(data, { merge: true });

    dispatch(
      'auth/new_notification',
      {
        type: 'notification',
        message: 'File uploaded successfully!',
        error: false,
      },
      { root: true }
    );

    const lines = rootGetters['lines/get_lines'];
    const market = entity.companyLocation.market;
    const selectedLine = lines.filter((val) => val.id === entity.line.id);

    if (selectedLine[0]?.markets[market]?.protocol === 'email') {
      const email = selectedLine[0]?.email[market] || null;
      // send email
      firebase
        .firestore()
        .collection('mail')
        .add({
          to: email,
          template: {
            name: 'pod_upload',
            data: {
              container: `${entity.container}`,
              created: `${getLocalTime().format('MM/DD/YYYY')}`,
              filename: `${name}`,
              filepath: `${src}`,
              line: entity.line.label,
              turn_url: `${process.env.FB_APP_DOMAIN}/network?id=${entity.turn_id}`,
            },
          },
        });
    }
    return podId;
  },

  // getting pod document form documents collection
  get_pod: async ({ commit }, payload) => {
    return await firebase
      .firestore()
      .collection('documents')
      .doc(payload)
      .get()
      .then((doc) => {
        return doc.data();
      });
  },

  // deleting pod document
  delete_pod: async ({ commit, dispatch }, payload) => {
    const turnRef = firebase
      .firestore()
      .collection('turns')
      .doc(payload.container + '_' + payload.orgId);
    const turnDoc = (await turnRef.get()).data();
    if (turnDoc.podDocId) {
      turnRef.update({
        podDocId: firebase.firestore.FieldValue.delete(),
      });
      firebase
        .firestore()
        .collection('documents')
        .doc(turnDoc.podDocId)
        .delete();
      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          message: 'File removed successfully ',
          error: false,
        },
        { root: true }
      );
    }
  },
};

const getters = {
  get_turns_upload_progress: (state) => state.progress,
  get_turns_snapshots: (state) => state.snapshots,
  get_secondary_carrier_snapshots: (state) => state.secondary_carrier_snapshots,
  get_turns: (state) => state.turns,
  get_tour_turns: (state) => state.tour_turns,
  get_locations_data: (state) => state.locations,
  get_approved_turns: (state) =>
    state.turns.filter((turn) => turn.status === 'approved'),
  get_pending_turns: (state) =>
    state.turns.filter((turn) => turn.status === 'pending'),
  get_declined_turns: (state) =>
    state.turns.filter((turn) => turn.status === 'declined'),
  get_error: (state) => state.error,
  total_pending: ({ turns }) => {
    let sum = 0;
    turns.forEach(({ status, amount }) => {
      if (status === 'pending') {
        sum += parseInt(amount);
      }
    });
    return sum;
  },
  total_approved: ({ turns }) => {
    let sum = 0;
    turns.forEach(({ status, amount }) => {
      if (status === 'approved') {
        sum += parseInt(amount);
      }
    });
    return sum;
  },
  total_declined: ({ turns }) => {
    let sum = 0;
    turns.forEach(({ status, amount }) => {
      if (status === 'declined') {
        sum += parseInt(amount);
      }
    });
    return sum;
  },
  get_is_scac_in_org: (state) => state.is_scac_in_org,
  get_secondary_carrier_turns: (state) => state.secondary_carrier_turns,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
